<style scoped>
.btn_shadow {
  background: white 0% 0% no-repeat padding-box;
  box-shadow: 4px 4px 10px #838383, -4px -4px 10px #ffffff !important;
}

.headline {
  background-color: #1b2643;
  width: 100%;
  height: 60px;
}

.active_text {
  border-bottom: 2px solid #ffffff;
  height: 60px;
}

.headline p {
  color: #ffffff;
  font-size: 14px;
}

.content {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
}

.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
}

.toggle-label {
  font-size: 14px;
  color: #333;
}

.toggle-description {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
}
</style>

<template>
  <div>
    <!-- Customer Settings Section -->
    <div>
      <div class="pa-3 headline">
        <p class="active_text">{{ $t('settings.title') }}</p>
      </div>

      <div class="content px-10">
        <!-- Loading state -->
        <v-row v-if="isLoading" justify="center" class="my-10">
          <v-progress-circular indeterminate color="#1b2643" />
        </v-row>

        <!-- Error state -->
        <v-row v-else-if="loadError" justify="center" class="mb-4 mt-6">
          <v-col cols="10">
            <v-alert type="error" icon="mdi-alert-circle">
              {{ $t('settings.loadError') }}
            </v-alert>
          </v-col>
        </v-row>

        <template v-else>
          <template v-for="(toggle, index) in toggleSettings">
            <v-row
              :key="toggle.field"
              :class="[
                {
                  'mt-8': index === 0,
                  'mb-6': index === toggleSettings.length - 1,
                },
              ]"
            >
              <v-col>
                <div class="toggle-row">
                  <div>
                    <div class="toggle-label">
                      {{ $t(toggle.labelKey) }}
                    </div>
                    <div class="toggle-description">
                      {{ $t(toggle.descriptionKey) }}
                    </div>
                  </div>
                  <v-switch
                    v-model="toggleValues[toggle.field]"
                    @change="updateSetting(toggle.field, $event)"
                    color="success"
                    inset
                    :loading="savingField === toggle.field"
                    :disabled="savingField !== null"
                  />
                </div>
              </v-col>
            </v-row>
            <v-divider
              v-if="index < toggleSettings.length - 1"
              :key="toggle.field + '-divider'"
              class="mx-10"
            />
          </template>
        </template>
      </div>
    </div>

    <!-- Security Settings Section -->
    <div class="mt-10" v-show="!showChangePassword">
      <div class="pa-3 headline">
        <p class="active_text">{{ $t('securitySettings.securitySetting') }}</p>
      </div>
      <div class="content px-10">
        <v-form @submit.prevent="verifyPassword" ref="form">
          <v-row class="mt-10">
            <p
              class="text-body-2"
              v-html="$t('securitySettings.canChangePassword')"
            ></p>
          </v-row>
          <v-row v-if="isSignInError" justify="center" class="mb-n7">
            <v-col cols="10">
              <v-alert icon="mdi-alert-circle" type="error">
                {{ $t('securitySettings.passwordIncorrect') }}
              </v-alert>
            </v-col>
          </v-row>
          <v-row justify="center">
            <v-col cols="10">
              <v-text-field
                :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showPassword ? 'text' : 'password'"
                @click:append="showPassword = !showPassword"
                v-model="password"
                :label="$t('common.password')"
                persistent-placeholder
                outlined
                required
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row justify="center" class="ma-4 mt-2">
            <v-btn color="#1b2643" type="submit" class="white--text btn_shadow">
              {{ $t('securitySettings.next') }}
            </v-btn>
          </v-row>
        </v-form>
      </div>
    </div>
    <ChangePasswordForm
      v-if="user"
      :show.sync="showChangePassword"
      :userAuth="{ user: user, email: email, password: currentPassword }"
    />

    <!-- Toast notifications for settings -->
    <snack-bar
      :snackbarAction="isSnackbarAction"
      :snackbarText="snackbarText"
      :snackbarColor="snackbarColor"
      @closeSnackbar="closeSnackbar"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from "@vue/composition-api";
import {
  EmailAuthProvider,
  getAuth,
  reauthenticateWithCredential,
} from "firebase/auth";
import { customerService } from "@/api/customerService";
import ChangePasswordForm from "@/components/Common/ChangePasswordForm.vue";
import SnackBar from "@/components/Dashboard/PageViewItems/SnackbarComponent.vue";
import store from "@/store";

export default defineComponent({
  name: "SettingsView",
  components: {
    ChangePasswordForm,
    SnackBar,
  },
  setup() {
    const auth = getAuth();
    const user = auth.currentUser;
    return { user, email: user?.email ?? "" };
  },
  data() {
    return {
      // Customer settings
      toggleValues: {
        showTranscript: true,
        showPostSurvey: true,
        showContactForm: true,
      } as Record<string, boolean>,
      isLoading: true,
      loadError: false,
      savingField: null as string | null,

      // Security settings
      password: "",
      currentPassword: "",
      showPassword: false,
      showChangePassword: false,
      isSignInError: false,

      // Snackbar
      isSnackbarAction: false,
      snackbarText: "",
      snackbarColor: "",
    };
  },
  computed: {
    toggleSettings(): {
      field: string;
      labelKey: string;
      descriptionKey: string;
    }[] {
      return [
        {
          field: "showTranscript",
          labelKey: "settings.showTranscript",
          descriptionKey: "settings.showTranscriptDescription",
        },
        {
          field: "showPostSurvey",
          labelKey: "settings.showPostSurvey",
          descriptionKey: "settings.showPostSurveyDescription",
        },
        {
          field: "showContactForm",
          labelKey: "settings.showContactForm",
          descriptionKey: "settings.showContactFormDescription",
        },
      ];
    },
  },
  methods: {
    async loadCustomerSettings() {
      const customerId = store.getters.customerId;
      if (!customerId) {
        this.loadError = true;
        this.isLoading = false;
        return;
      }

      try {
        const customer = await customerService.getById(customerId);
        if (customer) {
          this.toggleValues.showTranscript = customer.showTranscript ?? true;
          this.toggleValues.showPostSurvey = customer.showPostSurvey ?? true;
          this.toggleValues.showContactForm = customer.showContactForm ?? true;
        } else {
          this.loadError = true;
        }
      } catch (error) {
        console.error("Failed to load customer settings:", error);
        this.loadError = true;
      } finally {
        this.isLoading = false;
      }
    },

    async updateSetting(field: string, value: boolean) {
      const customerId = store.getters.customerId;
      if (!customerId) return;

      this.savingField = field;
      try {
        const success = await customerService.updateById(customerId, {
          [field]: value,
        });
        if (success) {
          this.showSuccessMessage(this.$t("settings.saveSuccess") as string);
        } else {
          this.toggleValues[field] = !value;
          this.showErrorMessage(this.$t("settings.saveError") as string);
        }
      } catch (error) {
        console.error(`Failed to update ${field}:`, error);
        this.toggleValues[field] = !value;
        this.showErrorMessage(this.$t("settings.saveError") as string);
      } finally {
        this.savingField = null;
      }
    },

    verifyPassword() {
      const auth = getAuth();
      const credential = EmailAuthProvider.credential(
        this.email,
        this.password,
      );
      if (!auth.currentUser) return;
      reauthenticateWithCredential(auth.currentUser, credential)
        .then((userCredential) => {
          this.user = userCredential.user;
          this.currentPassword = this.password;
          this.password = "";
          this.isSignInError = false;
          this.showChangePassword = true;
        })
        .catch(() => {
          this.isSignInError = true;
        });
    },

    showSuccessMessage(message: string) {
      this.snackbarText = message;
      this.snackbarColor = "success";
      this.isSnackbarAction = true;
    },
    showErrorMessage(message: string) {
      this.snackbarText = message;
      this.snackbarColor = "error";
      this.isSnackbarAction = true;
    },
    closeSnackbar() {
      this.isSnackbarAction = false;
      this.snackbarText = "";
      this.snackbarColor = "";
    },
  },
  async mounted() {
    await this.loadCustomerSettings();
  },
});
</script>
